/**
 * ⚠️ CMS-BOUND — this is regulatory CONTENT that the studio's CMS will own.
 * ----------------------------------------------------------------------------
 * The per-framework condition-field contract below (`SPECS`) is authored data,
 * not app logic: the CMS defines, per framework, WHICH condition fields exist
 * (their `key`, `label`, `required`, `hint`) as the operative content of each
 * framework's `conditions.rules` fragment. Today it lives here as code; in the
 * future consolidation (see the centralization report, step 3) it is served
 * from the CMS and this file becomes a thin reader. The AI pipeline fills these
 * fields; this UI renders and diffs them by framework slug + field `key`.
 *
 * Because the field KEYS are a content⇄UI contract, a mismatch (a field the CMS
 * renamed, or content carrying a key the UI's spec doesn't define) must NOT be
 * silently dropped — it is surfaced as a visible error. See `unknownFieldKeys`
 * and the <ConditionFieldErrors> component.
 *
 * Step 3 — Verify statements. The condition-field contract per framework.
 * Ported verbatim from the regulatory-cms studio (src/core/conditions.ts).
 *
 * A `compliant_with_conditions` verdict is only substantiated once the
 * substances that trigger it are connected to the articles in scope AND the
 * framework's required conditional data is captured. WHAT that data is varies
 * per framework — it is the operative content of each framework's
 * `conditions.rules` CMS fragment (the named gaps there). This module encodes
 * that contract as a structured spec the step-3 UI binds to by framework slug.
 *
 * Two axes, per the domain:
 *  - **Universal** — the substance identifier (the registry connection itself)
 *    and its **concentration** in the article. True irrespective of framework,
 *    so it is captured once per (article × substance) and shared across every
 *    framework obligation that presence triggers (the pivot).
 *  - **Framework-specific** — the datum that turns "present" into "compliant
 *    with conditions" for THIS regime (Art. 33 safe-use, the Annex III/IV
 *    exemption number, the Annex XVII entry, the authorisation, the POP limit…).
 *    Captured per (framework × article × substance): the same shape the master
 *    data records (article_substance_links.fields), so step 3 can diff the
 *    declaration's values against what is currently on record.
 *
 * Structure lives here (code, fingerprint-free consumption layer); the wording
 * stays in the CMS `conditions.rules`. Promoting this to a structured CMS
 * fragment (`conditions.fields`, json) is the natural seam-1 iteration.
 */

import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { Verdict } from "./studioTypes"

/** One choice of a `"select"` condition field. */
export interface ConditionFieldOption {
  value: string
  label: TranslationKeys
}

/** One captured field of a framework's condition contract. `label`/`hint` are
 *  translation keys — the CMS-authored wording is content, but the reviewer
 *  UI is localized, so it must be resolved through `translate()`
 *  at the render site rather than hardcoded here. */
export interface ConditionField {
  key: string
  label: TranslationKeys
  required: boolean
  hint?: TranslationKeys
  /** Render hint for the step-3 UI: `"date"` renders a native
   *  `<input type="date">` instead of a plain text box; `"select"` renders a
   *  dropdown over `options`. The PERSISTED shape is unchanged either way, a
   *  plain string (an ISO `YYYY-MM-DD` date, or one of the options' `value`s),
   *  so this is purely a rendering choice, never a schema change. A persisted
   *  value that is not one of the options (legacy free text) is shown as it is.
   *  Optional, default `"text"`, so every field without a bespoke kind keeps
   *  today's behaviour. */
  kind?: "text" | "date" | "select"
  /** The fixed choices of a `"select"` field. `value` is what is stored and
   *  what the backend's enum option (cms-snapshot `options`) is; `label` is the
   *  translation key shown to the reviewer. */
  options?: ConditionFieldOption[]
  /** Backend twin of `result_spec.required_for` (only the value the mask
   *  reasons about): `"verdict"` marks the field whose value establishes the
   *  verdict basis (rohs exemption number, china-rohs EFUP). Once such a
   *  field is filled, the universal concentration is a completeness datum
   *  and no longer reads as required. Absent = no role in that derivation. */
  requiredFor?: "verdict" | "completeness"
}

/** A framework's full condition contract: the universal concentration + its
 *  framework-specific fields. `concentration: null` = the regime is not
 *  substance-concentration based (halogen-free is elemental) — its fields are
 *  then captured per article, not per substance. */
export interface ConditionSpec {
  /** The universal concentration datum (denominator differs per framework —
   *  stated in the label), or null when the regime does not use one. */
  concentration: { label: TranslationKeys; required: boolean } | null
  fields: ConditionField[]
}

const GENERIC: ConditionSpec = {
  concentration: { label: "MC_DECL_COND_GENERIC_CONC_LABEL", required: true },
  fields: [
    {
      key: "info",
      label: "MC_DECL_COND_GENERIC_INFO_LABEL",
      required: false,
      hint: "MC_DECL_COND_GENERIC_INFO_HINT",
    },
  ],
}

// One entry per framework. Fields mirror the named gaps in each framework's
// `conditions.rules` fragment; the substance identity/CAS come from the
// registry connection and the affected articles express the "location", so
// neither is repeated here.
const SPECS: Record<string, ConditionSpec> = {
  "reach-svhc": {
    concentration: {
      label: "MC_DECL_COND_REACH_SVHC_CONC_LABEL",
      required: true,
    },
    fields: [
      {
        key: "safe_use_info",
        label: "MC_DECL_COND_REACH_SVHC_SAFE_USE_INFO_LABEL",
        required: false,
        hint: "MC_DECL_COND_REACH_SVHC_SAFE_USE_INFO_HINT",
      },
      {
        key: "scip_number",
        label: "MC_DECL_COND_REACH_SVHC_SCIP_NUMBER_LABEL",
        required: false,
        hint: "MC_DECL_COND_REACH_SVHC_SCIP_NUMBER_HINT",
      },
    ],
  },
  "reach-annex14": {
    concentration: {
      label: "MC_DECL_COND_REACH_ANNEX14_CONC_LABEL",
      required: false,
    },
    fields: [
      {
        key: "authorisation",
        label: "MC_DECL_COND_REACH_ANNEX14_AUTHORISATION_LABEL",
        required: true,
        hint: "MC_DECL_COND_REACH_ANNEX14_AUTHORISATION_HINT",
      },
    ],
  },
  "reach-annex17": {
    concentration: {
      label: "MC_DECL_COND_REACH_ANNEX17_CONC_LABEL",
      required: true,
    },
    fields: [
      {
        key: "entry_number",
        label: "MC_DECL_COND_REACH_ANNEX17_ENTRY_NUMBER_LABEL",
        required: true,
        hint: "MC_DECL_COND_REACH_ANNEX17_ENTRY_NUMBER_HINT",
      },
      {
        key: "conditions_met",
        label: "MC_DECL_COND_REACH_ANNEX17_CONDITIONS_MET_LABEL",
        required: true,
        hint: "MC_DECL_COND_REACH_ANNEX17_CONDITIONS_MET_HINT",
      },
    ],
  },
  rohs: {
    concentration: {
      label: "MC_DECL_COND_ROHS_CONC_LABEL",
      required: true,
    },
    fields: [
      {
        key: "exemption_number",
        label: "MC_DECL_COND_ROHS_EXEMPTION_NUMBER_LABEL",
        required: true,
        hint: "MC_DECL_COND_ROHS_EXEMPTION_NUMBER_HINT",
        requiredFor: "verdict",
      },
      {
        key: "exemption_expiry",
        label: "MC_DECL_COND_ROHS_EXEMPTION_EXPIRY_LABEL",
        required: false,
        hint: "MC_DECL_COND_ROHS_EXEMPTION_EXPIRY_HINT",
        kind: "date",
      },
    ],
  },
  pop: {
    concentration: { label: "MC_DECL_COND_POP_CONC_LABEL", required: true },
    fields: [
      {
        key: "limit_value",
        label: "MC_DECL_COND_POP_LIMIT_VALUE_LABEL",
        required: true,
        hint: "MC_DECL_COND_POP_LIMIT_VALUE_HINT",
      },
      {
        key: "derogation_entry",
        label: "MC_DECL_COND_POP_DEROGATION_ENTRY_LABEL",
        required: false,
      },
      {
        key: "derogation_expiry",
        label: "MC_DECL_COND_POP_DEROGATION_EXPIRY_LABEL",
        required: false,
        kind: "date",
      },
    ],
  },
  pfas: {
    concentration: { label: "MC_DECL_COND_PFAS_CONC_LABEL", required: true },
    fields: [
      {
        key: "function",
        label: "MC_DECL_COND_PFAS_FUNCTION_LABEL",
        required: false,
      },
      {
        key: "limit_value",
        label: "MC_DECL_COND_PFAS_LIMIT_VALUE_LABEL",
        required: false,
        hint: "MC_DECL_COND_PFAS_LIMIT_VALUE_HINT",
      },
      {
        key: "derogation_entry",
        label: "MC_DECL_COND_PFAS_DEROGATION_ENTRY_LABEL",
        required: false,
      },
    ],
  },
  tsca: {
    concentration: { label: "MC_DECL_COND_TSCA_CONC_LABEL", required: true },
    fields: [
      {
        key: "presence_basis",
        label: "MC_DECL_COND_TSCA_PRESENCE_BASIS_LABEL",
        required: true,
      },
      {
        key: "carveout_citation",
        label: "MC_DECL_COND_TSCA_CARVEOUT_CITATION_LABEL",
        required: true,
        hint: "MC_DECL_COND_TSCA_CARVEOUT_CITATION_HINT",
      },
    ],
  },
  cp65: {
    concentration: {
      label: "MC_DECL_COND_CP65_CONC_LABEL",
      required: false,
    },
    fields: [
      {
        key: "endpoint",
        label: "MC_DECL_COND_CP65_ENDPOINT_LABEL",
        // Interim: optional, so a statement whose supplier says no warning is
        // required is not blocked by an empty endpoint. Twin of the backend cp65
        // spec; the conditional rule (required unless exemption_basis) is the follow-up.
        required: false,
        kind: "select",
        // Twin of the cp65 `endpoint` enum in the backend cms-snapshot: the same
        // four strings, in the same order.
        options: [
          { value: "cancer", label: "MC_DECL_COND_CP65_ENDPOINT_CANCER" },
          {
            value: "reproductive toxicity",
            label: "MC_DECL_COND_CP65_ENDPOINT_REPRODUCTIVE",
          },
          {
            value: "cancer and reproductive toxicity",
            label: "MC_DECL_COND_CP65_ENDPOINT_BOTH",
          },
          {
            value: "not listed",
            label: "MC_DECL_COND_CP65_ENDPOINT_NOT_LISTED",
          },
        ],
      },
      {
        key: "exemption_basis",
        label: "MC_DECL_COND_CP65_EXEMPTION_BASIS_LABEL",
        required: false,
        hint: "MC_DECL_COND_CP65_EXEMPTION_BASIS_HINT",
      },
      {
        key: "exposure_assessment",
        label: "MC_DECL_COND_CP65_EXPOSURE_ASSESSMENT_LABEL",
        required: false,
        hint: "MC_DECL_COND_CP65_EXPOSURE_ASSESSMENT_HINT",
      },
      {
        key: "warning_text",
        label: "MC_DECL_COND_CP65_WARNING_TEXT_LABEL",
        required: false,
      },
    ],
  },
  "china-rohs": {
    concentration: {
      label: "MC_DECL_COND_CHINA_ROHS_CONC_LABEL",
      required: true,
    },
    fields: [
      {
        key: "efup_years",
        label: "MC_DECL_COND_CHINA_ROHS_EFUP_YEARS_LABEL",
        required: true,
        requiredFor: "verdict",
      },
      {
        key: "xo_table",
        label: "MC_DECL_COND_CHINA_ROHS_XO_TABLE_LABEL",
        required: false,
        hint: "MC_DECL_COND_CHINA_ROHS_XO_TABLE_HINT",
      },
      {
        key: "catalogue_assessment",
        label: "MC_DECL_COND_CHINA_ROHS_CATALOGUE_ASSESSMENT_LABEL",
        required: false,
        hint: "MC_DECL_COND_CHINA_ROHS_CATALOGUE_ASSESSMENT_HINT",
      },
    ],
  },
  "halogen-free": {
    // Elemental — no substance concentration; the fields ARE the measurement.
    concentration: null,
    fields: [
      {
        key: "cl_ppm",
        label: "MC_DECL_COND_HALOGEN_FREE_CL_PPM_LABEL",
        required: true,
      },
      {
        key: "br_ppm",
        label: "MC_DECL_COND_HALOGEN_FREE_BR_PPM_LABEL",
        required: true,
      },
      {
        key: "total_ppm",
        label: "MC_DECL_COND_HALOGEN_FREE_TOTAL_PPM_LABEL",
        required: false,
      },
      {
        key: "standard",
        label: "MC_DECL_COND_HALOGEN_FREE_STANDARD_LABEL",
        required: false,
        hint: "MC_DECL_COND_HALOGEN_FREE_STANDARD_HINT",
      },
      {
        key: "test_method",
        label: "MC_DECL_COND_HALOGEN_FREE_TEST_METHOD_LABEL",
        required: false,
        hint: "MC_DECL_COND_HALOGEN_FREE_TEST_METHOD_HINT",
      },
    ],
  },
}

/** The condition contract for a framework — a generic single-field fallback
 *  for any framework without a bespoke spec (keeps future regimes usable). */
export function conditionSpec(frameworkSlug: string): ConditionSpec {
  return SPECS[frameworkSlug] ?? GENERIC
}

/** Per framework, the keys of its fields marked `requiredFor: "verdict"` — only
 *  frameworks that have one. The server's live verdict-basis check
 *  (`mc_declaration_cell_state.py::LIVE_VERDICT_BASIS_FIELDS`) holds a copy;
 *  `__fixtures__/verdictBasisFields.json` keeps the two equal. */
export function verdictBasisFieldKeys(): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const [slug, spec] of Object.entries(SPECS)) {
    const keys = spec.fields
      .filter((field) => field.requiredFor === "verdict")
      .map((field) => field.key)
    if (keys.length) out[slug] = keys
  }
  return out
}

/**
 * B6 (TUR-1211, tester finding Pablo 2026-09-14): a `non_compliant` verdict
 * cannot, by definition, also carry COMPLETE required conditional data, a
 * statement with every required field filled would be `compliant` or
 * `compliant_with_conditions`, not `non_compliant`. The mask therefore
 * treats every condition field (concentration and the framework's own
 * fields) as optional on a non_compliant statement: no star, no amber
 * border, no open-fields chip. This is a MASK RULE ONLY, the SPEC objects
 * above (`required`, `requiredFor`) are untouched, and so is the backend M6
 * `required_for` vocabulary and the approve-gate resolver; both already
 * never emit a required-field finding for a non-compliant cell.
 */
export function fieldsRequiredFor(verdict: Verdict | null): boolean {
  return verdict !== "non_compliant"
}

// ── Persisted shape (runs.condition_data) ────────────────────────────────────

/**
 * All of step 3's human curation for one run.
 *  - `concentration` — universal, keyed per (article × substance) via
 *    presenceKey. Framework-independent (the concentration of a substance in an
 *    article is one physical fact), so it is shared across every framework
 *    obligation that (article, substance) pair triggers — the pivot.
 *  - `fields` — framework-specific, keyed per (framework × article × substance)
 *    via fieldsKey: a record of the spec's field values. Substance-independent
 *    regimes (spec.concentration === null) key with a null substance.
 *  - `verified` — the human's explicit "this transition is verified" mark,
 *    keyed per (framework × article) via verifiedKey: one sign-off per row of
 *    the step-3 review, whatever its tier.
 */
export interface ConditionData {
  concentration: Record<string, string>
  fields: Record<string, Record<string, string>>
  verified: Record<string, boolean>
}

export function emptyConditionData(): ConditionData {
  return { concentration: {}, fields: {}, verified: {} }
}

export function parseConditionData(json: string | null): ConditionData {
  if (!json) return emptyConditionData()
  try {
    const raw = JSON.parse(json) as Partial<ConditionData>
    return {
      concentration: raw.concentration ?? {},
      fields: raw.fields ?? {},
      verified: raw.verified ?? {},
    }
  } catch {
    return emptyConditionData()
  }
}

/** The presence key: one substance's concentration in one article (framework-independent). */
export function presenceKey(articleId: string, substanceKey: string): string {
  return `${articleId}::${substanceKey}`
}

/** The fields key: one framework's condition values on one (article × substance).
 *  Pass null for substance-independent regimes (spec.concentration === null). */
export function fieldsKey(
  frameworkSlug: string,
  articleId: string,
  substanceKey: string | null,
): string {
  return `${frameworkSlug}|${articleId}|${substanceKey ?? "-"}`
}

/** The verified key: the human sign-off on one transition row (framework × article). */
export function verifiedKey(frameworkSlug: string, articleId: string): string {
  return `${frameworkSlug}|${articleId}`
}

/**
 * Is a conditional transition row's required data complete? Only a
 * `compliant_with_conditions` target demands data: the triggering substances
 * connected, every connected presence carrying a concentration (when the spec
 * requires one), and every required framework field filled. The `verified`
 * mark is the human sign-off, separate from completeness.
 */
export function rowComplete(
  spec: ConditionSpec,
  next: string,
  data: ConditionData,
  frameworkSlug: string,
  articleId: string,
  connectedSubstanceKeys: string[],
): boolean {
  if (next !== "compliant_with_conditions") return true
  const requiredFilled = (subKey: string | null) => {
    const fields =
      data.fields[fieldsKey(frameworkSlug, articleId, subKey)] ?? {}
    return spec.fields.every(
      (f) => !f.required || (fields[f.key] ?? "").trim() !== "",
    )
  }
  if (spec.concentration === null) return requiredFilled(null)
  if (connectedSubstanceKeys.length === 0) return false
  return connectedSubstanceKeys.every(
    (subKey) =>
      (!spec.concentration?.required ||
        (data.concentration[presenceKey(articleId, subKey)] ?? "").trim() !==
          "") &&
      requiredFilled(subKey),
  )
}

// ── CMS contract guard: field-key mismatch detection ─────────────────────────

/**
 * Field keys present in a framework's CONTENT (captured condition data and/or
 * the on-record master link) but NOT defined by its `ConditionSpec` — a
 * renamed/misnamed field the UI would otherwise drop silently.
 *
 * The field `key` is the content⇄UI contract (the CMS names it; the pipeline
 * fills it; this UI renders it). A key the spec doesn't know means data is going
 * unrendered — a real error, surfaced by <ConditionFieldErrors>, never skipped.
 *
 * NB: an UNKNOWN framework falling back to the GENERIC spec is NOT an error —
 * that is the designed path that keeps a not-yet-specced regime usable. This
 * guard is only about field-KEY drift within a framework that does have content.
 */
export function unknownFieldKeys(
  spec: ConditionSpec,
  contentKeys: Iterable<string>,
): string[] {
  const known = new Set(spec.fields.map((f) => f.key))
  const out: string[] = []
  for (const key of contentKeys) {
    if (key && !known.has(key) && !out.includes(key)) out.push(key)
  }
  return out
}
